/**
* @summary - Contract Management Parent File
* @author Yuvasri S
* @since 2024-11-29
*/
import React, { useRef, useState } from 'react';
import ContractForm from './ContractForm';
import ContractEditForm from './ContractEditForm';
import ContractAttachment from './ContractAttachment';
import { Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ContractSubFormGrid from './ContractSubFormGrid';
import CustomDialog from '../commoncomponents/CustomDialog';

const ContractManagementCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location?.state?.editmode;
    const viewMode = location?.state?.viewMode;
    const checkIsRenewalForm = location?.state?.isRenewalForm ? true : false;
    const contractMode = location?.state?.contractMode;
    const isCreateMode = location?.state?.createMode;
    const contractFormRef = useRef(null);
    const contractEditFormRef = useRef(null);
    const contractSubFormGridRef = useRef(null);
    const ContractAttachmentRef = useRef(null);

    const [selectedService, setSelectedService] = useState(null);

    const [rowLength, setRowLength] = useState(0);

    const [totalPPMRate, setTotalPPMRate] = useState(0);

    const handleServiceChange = (service) => {
        setSelectedService(service.value);
    };

    const [dialogContent, setDialogContent] = useState("");
    const [openDialog, setOpenDialog] = useState(false);

    const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);

    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const handleSave = async () => {
        let serviceType, headerId, parentContractId;

        try {
            let isEditFormSaved = false;
            let isSubFormSaved = false;

            if(contractMode || isCreateMode){
                const validation = await contractFormRef.current.validateFields();
                const subFormValidation = await contractSubFormGridRef.current.handleValidation();
                if (validation === true && subFormValidation === true) {
                    if (contractFormRef.current) {
                        serviceType = contractFormRef.current.serviceType;

                        const formResponse = await contractFormRef.current.handleSubmit();                        
                        if (formResponse && formResponse.id) {
                            headerId = formResponse.id;
                            isEditFormSaved = true;
                        } else if (formResponse?.status === 400) {
                            // Handle specific API error
                            setDialogContent(formResponse.response?.data?.detail || "Contract No already exists.");
                            setOpenDialog(true);
                            return; // Exit on error
                        } else {
                            throw new Error("Form submission failed or returned undefined response");
                        }
                    }

                    if (contractSubFormGridRef.current && serviceType && headerId) {
                        const subForm = await contractSubFormGridRef.current.handleSubFormSubmit(serviceType, headerId);
                        if (subForm[0]?.id) {
                            isSubFormSaved = true;
                        } else {
                            throw new Error("Grid submission failed or returned undefined response");
                        }
                    }

                    if (ContractAttachmentRef.current && headerId) {
                        await ContractAttachmentRef.current.handleSaveClick(headerId);
                    }

                    if (isEditFormSaved && isSubFormSaved) {
                        setDialogContent(isCreateMode ? "Contract created successfully." : "Data saved successfully.");
                        setOpenDialog(true);
                        setIsSaveDisabled(true);
                    }
                }
            } else {
                const validation = await contractEditFormRef.current.validateFields();
                const subFormValidation = await contractSubFormGridRef.current.handleValidation();                
                if (validation === true && subFormValidation === true) {
                    if (contractEditFormRef.current) {
                        serviceType = contractEditFormRef.current.serviceType;
                        headerId = contractEditFormRef.current.oldId;
                        parentContractId = contractEditFormRef.current.parentContractId;
                        const formResponse = await contractEditFormRef.current.handleSubmit();
                        
                        if (formResponse?.id) {
                            isEditFormSaved = true;
                            const subForm = await contractSubFormGridRef.current.handleSubFormSubmit(serviceType, parentContractId);
                            if (subForm[0]?.id) {
                                isSubFormSaved = true;
                            } else {
                                throw new Error("Grid submission failed or returned undefined response");
                            }
                            await ContractAttachmentRef.current.handleSaveClick(parentContractId);

                            if (isEditFormSaved && isSubFormSaved) {
                                setDialogContent(checkIsRenewalForm ? "Contract renewed successfully." : "Data saved successfully.");
                                setOpenDialog(true);
                                setIsSaveDisabled(true);
                            }
                        } else if (formResponse?.status === 400) {
                            // Handle specific API error
                            setDialogContent(formResponse.response?.data?.detail || "Contract No already exists.");
                            setOpenDialog(true);
                            return; // Exit on error
                        } else {
                            throw new Error("Form submission failed or returned undefined response");
                        }

                    }


                }
            }
        } catch (error) {
            console.error("Error during save:", error);

        }
    };



    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    const handleCancel = () => {
        if (contractFormRef.current?.hasUnsavedChanges()) {
            setUnsavedChangesDialog(true)// Show confirmation dialog
        } else {
            navigate(-1, { replace: true }); // Navigate back
        }
    };

    const confirmCancel = () => {
        setUnsavedChangesDialog(false);
        navigate(-1, { replace: true }); // Navigate back
    };

    const cancelCloseDialog = () => {
        setUnsavedChangesDialog(false);
    };

    return (
        <>
            {location?.state?.newcontractid || location?.state?.isRenewalForm ? (
                <ContractEditForm
                    ref={contractEditFormRef}
                    totalPPMRate={totalPPMRate}
                    viewMode={viewMode} 
                />
            ) : (
                <ContractForm
                    ref={contractFormRef}
                    onServiceChange={handleServiceChange}
                    totalPPMRate={totalPPMRate}
                />
            )}


            <Box mb={2} />
            <ContractSubFormGrid ref={contractSubFormGridRef} service={selectedService} setRowLength={(item) => setRowLength(item)}
                setTotalPPMRate={(total) => setTotalPPMRate(total)} />
            <Box mb={2} />
            <ContractAttachment ref={ContractAttachmentRef} service={selectedService} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {!viewMode && (
        <Button
            variant="contained"
            color="primary"
            sx={{ m: 1 }}
            onClick={handleSave}
            disabled={isSaveDisabled}
        >
            {(editMode && !checkIsRenewalForm) ? "Update" : "Save"}
        </Button>
    )}
                <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                    Back
                </Button>
            </Box>

            {/* Success Dialog */}
            <CustomDialog
                open={openDialog}
                onClose={handleCloseDialog}
                content={dialogContent}
                confirmBtn="OK"
                onConfirm={handleCloseDialog}
            />
            <CustomDialog
                open={unsavedChangesDialog}
                onClose={cancelCloseDialog}
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={confirmCancel}
                onCancel={cancelCloseDialog}
            />
        </>
    );
};

export default ContractManagementCreate;
